import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
import { SiteAlertState } from './evacuation.js';
/** Module `reception-display` (docs/modules/reception-display.md, ADR 0022, spec 8.6.11). Owner: task S35. */
/** Public: POST /devices/display-pairing (core, task C4). */
export const DisplayPairingStart = z.object({ userAgent: z.string().max(300).default('') });
export const DisplayPairingCode = z.object({ code: z.string().regex(/^\d{9}$/), pollSecret: z.string(), expiresAt: IsoDateTime, pollEverySeconds: z.number().int() });
export const DisplayPairingPoll = z.object({ pollSecret: z.string().min(20).max(200) });
export const DisplayPairingPollResult = z.discriminatedUnion('status', [
    z.object({ status: z.literal('waiting') }),
    z.object({ status: z.literal('expired') }),
    /** Device token, shown once; the display keeps it in localStorage and sends `Authorization: Device <token>`. */
    z.object({ status: z.literal('paired'), token: z.string(), deviceId: Uuid, siteId: Uuid }),
]);
/** Portal: POST /reception-display/claim */
export const DisplayClaim = z.object({ code: z.string().regex(/^\d{9}$/), siteId: Uuid, name: z.string().trim().min(1).max(80) });
export const DisplayContent = z.object({
    welcome: z.boolean(),
    welcomeWindowMinutes: z.number().int(),
    headcount: z.boolean(),
    capacity: z.boolean(),
    notices: z.boolean(),
    clock: z.boolean(),
    /** 'first_name' (default), 'first_name_company', 'none'. */
    names: z.enum(['first_name', 'first_name_company', 'none']),
});
export const DisplaySettingsPut = DisplayContent.partial().extend({ message: z.string().max(300).optional() });
export const DisplayDevice = z.object({ deviceId: Uuid, siteId: Uuid, name: z.string(), content: DisplayContent, message: z.string(), lastSeenAt: IsoDateTime.nullable(), status: z.enum(['active', 'revoked']) });
/** GET /reception-display/device/view: everything the screen shows. */
export const DisplayView = z.object({
    siteName: z.string(),
    timeZone: z.string(),
    branding: z.object({ logoUrl: z.string().nullable(), backgroundColour: z.string(), textColour: z.string() }),
    content: DisplayContent,
    message: z.string(),
    welcome: z.array(z.object({ firstName: z.string(), company: z.string().nullable(), expectedAt: IsoDateTime })),
    headcount: z.number().int().nullable(),
    capacity: z.object({ capacity: z.number().int(), level: z.enum(['none', 'green', 'amber', 'red']) }).nullable(),
    notices: z.array(z.object({ title: z.string(), text: z.string() })),
    /** Rotating contactless QR when the site uses rotating mode and this display shows it. */
    contactlessQr: z.object({ url: z.string(), expiresAt: IsoDateTime }).nullable(),
    alert: SiteAlertState,
    generatedAt: IsoDateTime,
});
/** Live channel `display` (devices of kind display): `{ type: 'refresh' }` after anything on the view changes. */
export const DisplayLiveMessage = z.object({ type: z.literal('refresh') });
export const ReceptionDisplaySiteSettings = z.object({
    welcome: z.boolean().default(true),
    welcomeWindowMinutes: z.number().int().min(0).max(240).default(30),
    /** Keep an expected visitor listed this long after the expected time if not yet arrived (0 = drop at once). */
    welcomeLateMinutes: z.number().int().min(0).max(120).default(15),
    headcount: z.boolean().default(true),
    capacity: z.boolean().default(false),
    notices: z.boolean().default(false),
    clock: z.boolean().default(true),
    names: z.enum(['first_name', 'first_name_company', 'none']).default('first_name'),
    /** Show the rotating contactless QR (ADR 0022). */
    showContactlessQr: z.boolean().default(false),
    /** Notices from the notice library shown in the carousel (text only, through the notices service). */
    noticeIds: z.array(Uuid).max(10).default([]),
});
//# sourceMappingURL=reception-display.js.map